<template>
  <SideMenuExpanded
    id="side-menu-navigation"
    class="absolute left-0 lg:flex lg:static top-0 z-40"
    :class="{
      'hidden! lg:flex!': !isMobileSidebarVisible,
      flex: isMobileSidebarVisible,
    }"
    :menu="menu"
    @close="() => closeMobileSidebar()">
    <template #parent-menu-item="{item}">
      <ParentMenuItemExpanded
        :active-route-name="route.name"
        :item="item">
        {{ getLabel(item) }}

        <template #external-menu-item="{child}">
          <ExternalMenuItem :item="child">
            {{ getLabel(child) }}
          </ExternalMenuItem>
        </template>

        <template #internal-menu-item="{child}">
          <InternalMenuItem
            :is-active="child.to.name === route.name || child.activeRouteNameAliases?.includes(route.name)"
            :item="child"
            :link-component="RouterLink">
            {{ getLabel(child) }}
          </InternalMenuItem>
        </template>
      </ParentMenuItemExpanded>
    </template>

    <template #external-menu-item="{item}">
      <ExternalMenuItem :item="item">
        {{ getLabel(item) }}
      </ExternalMenuItem>
    </template>

    <template #internal-menu-item="{item}">
      <InternalMenuItem
        :is-active="item.to.name === route.name || item.activeRouteNameAliases?.includes(route.name)"
        :item="item"
        :link-component="RouterLink">
        {{ getLabel(item) }}
      </InternalMenuItem>
    </template>

    <template #divider-menu-item>
      <DividerMenuItem />
    </template>

    <ul>
      <DividerMenuItem class="my-4" />
      <InternalMenuItem
        class="py-0! text-left text-sm"
        hide-icon
        :item="{
          type: 'internal',
          label: 'settings.account.title',
          to: {name: 'account-settings-my-contact-info'},
        }"
        link-class="py-1!"
        :link-component="RouterLink">
        {{ t('settings.account.title', 2) }}
      </InternalMenuItem>
      <li>
        <Button
          id="logout-button"
          class="cursor-pointer hover:bg-gray-200 px-4 py-1 text-left text-red-600 text-sm w-full whitespace-nowrap"
          size="none"
          variant="none"
          @click="() => logout()">
          {{ t('account.index.logout') }}
        </Button>
      </li>
      <DividerMenuItem class="my-4" />
      <li class="flex my-3 pl-1 pr-3 justify-center lg:justify-between">
        <CollapseMenuButton
          :aria-label="t('ui.navigation.collapse')"
          class="hover:bg-transparent! hidden lg:block"
          :is-collapsed="isSidebarCollapsed"
          @click="() => collapseSidebar()" />
        <LanguageToggle
          :languages="languages"
          @change-language="changeLanguage" />
      </li>
    </ul>
  </SideMenuExpanded>
</template>

<script setup lang="ts">
import type {InternalMenuItemConfig} from '@/composables/useMenu/useMenu';
import {useProposition} from '@myparcel-frontend/proposition';
import {
  SideMenuExpanded,
  InternalMenuItem,
  ExternalMenuItem,
  ParentMenuItemExpanded,
  DividerMenuItem,
  CollapseMenuButton,
  Button,
  LanguageToggle,
} from '@myparcel-frontend/ui';
import {language} from '@myparcel-frontend/utils';
import {watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {RouterLink, useRoute} from 'vue-router';
import useChangeLanguage from '../../../../composables/useChangeLanguage/useChangeLanguage';
import useAuth from '@/composables/useAuth';
import useMenu from '@/composables/useMenu';
import useShop from '@/composables/useShop/useShop';
import useSidebarToggle from '@/composables/useSidebarToggle';

const {currentShop} = useShop();
const {menu, getMenu} = useMenu();
const {isSidebarCollapsed, collapseSidebar, isMobileSidebarVisible, closeMobileSidebar} = useSidebarToggle();
const {logout} = useAuth();
const {t} = useI18n();
const {changeLanguage} = useChangeLanguage();
const {proposition} = useProposition();
const route = useRoute();

const getLabel = (item: InternalMenuItemConfig) => {
  return t(item.label, item?.multiplyLabel ? 2 : 1);
};

const languages = language.formatLanguageOptions(
  proposition.value.applications.backoffice.internationalization.supportedLanguages,
  proposition.value.applications.backoffice.internationalization.language,
);

watch(currentShop, () => getMenu(), {
  immediate: true,
});
</script>
